import { useEffect, useRef, useState } from 'react'

/**
 * Throttles a rapidly-changing value to reduce downstream re-renders.
 * When disabled, the value passes through with zero overhead.
 * On transition from enabled→disabled, the latest value is flushed immediately.
 */
export const useThrottledValue = <T>(value: T, intervalMs: number, isEnabled: boolean): T => {
  const [throttledValue, setThrottledValue] = useState<T>(value)
  const latestRef = useRef<T>(value)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  latestRef.current = value

  useEffect(() => {
    if (!isEnabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      setThrottledValue(latestRef.current)
      return
    }

    if (timerRef.current) {
      return
    }

    setThrottledValue(value)

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setThrottledValue(latestRef.current)
    }, intervalMs)
  }, [value, intervalMs, isEnabled])

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    },
    []
  )

  if (!isEnabled) {
    return value
  }

  return throttledValue
}
